import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { LANDING } from "@/constants/testIds";

/**
 * Funds (/funds) — full Fund Architecture detail page.
 * Mirrors the homepage Fund Architecture section but with
 * extended capacity / fee / structure detail per vehicle.
 */

const VEHICLES = [
  {
    family: "Fund",
    name: "KR Credit Opportunities Fund",
    mandate: "Industrial Credit",
    term: "Open-ended evergreen",
    domicile: "Cayman Islands",
    targetSize: "USD 750m – 1.0bn",
    minCommit: "USD 10m",
    fees: "Mgmt: in formation · Carry: institutional",
    note:
      "Senior-secured and unitranche financings across the lower-mid-market industrial complex. Asset-backed underwriting against tangible collateral, contracted utilisation and recurring revenue characteristics.",
  },
  {
    family: "Fund",
    name: "KR Special Situations Fund I",
    mandate: "Bridge & Special Situations",
    term: "Closed-ended · 7+1+1",
    domicile: "Cayman Islands",
    targetSize: "USD 400m – 600m",
    minCommit: "USD 10m",
    fees: "Mgmt: in formation · Carry: institutional",
    note:
      "Time-sensitive financings, rescue capital and capital-event bridges with covenants and security calibrated to the exit, not the promise.",
  },
  {
    family: "Fund",
    name: "KR Strategic Equity Fund I",
    mandate: "Opportunistic Industrial Equity",
    term: "Closed-ended · 10+1+1",
    domicile: "Cayman Islands",
    targetSize: "USD 750m – 1.2bn",
    minCommit: "USD 10m",
    fees: "Mgmt: in formation · Carry: institutional",
    note:
      "Control and influence equity in asset-rich industrial businesses with operational and value-realisation pathways.",
  },
  {
    family: "Fund",
    name: "KR Real Estate Credit Fund I",
    mandate: "Industrial Real Estate Credit",
    term: "Closed-ended initially · 6+1+1",
    domicile: "Cayman Islands",
    targetSize: "USD 500m – 750m",
    minCommit: "USD 10m",
    fees: "Mgmt: in formation · Carry: institutional",
    note:
      "Asset-backed senior-secured loans against industrial, logistics and operationally critical real estate.",
  },
  {
    family: "Fund",
    name: "KR Real Assets Fund I",
    mandate: "Real Assets · Industrial",
    term: "Closed-ended · 10+1+1",
    domicile: "Cayman Islands",
    targetSize: "USD 500m – 800m",
    minCommit: "USD 10m",
    fees: "Mgmt: in formation · Carry: institutional",
    note:
      "Equity and equity-linked exposure to tangible industrial assets with contracted utilisation characteristics.",
  },
  {
    family: "Fund",
    name: "KR Infrastructure Fund I",
    mandate: "Industrial Infrastructure",
    term: "Long-duration closed-ended · 12+1+1",
    domicile: "Cayman Islands",
    targetSize: "USD 1.0bn – 1.5bn",
    minCommit: "USD 25m",
    fees: "Mgmt: in formation · Carry: institutional",
    note:
      "Patient capital for long-duration infrastructure assets across energy transition, logistics and onshoring.",
  },
  {
    family: "Capital Channel",
    name: "KR Co-Investment Vehicles",
    mandate: "Cross-Mandate",
    term: "Deal-by-deal SPVs",
    domicile: "Cayman Islands · Bespoke",
    targetSize: "Per transaction",
    minCommit: "By invitation",
    fees: "Reduced / no carry, by class",
    note:
      "Single-asset SPVs alongside the platform funds, offered selectively to limited partners and strategic counterparties.",
  },
  {
    family: "Business Line",
    name: "KR Debt Solutions",
    mandate: "Industrial Credit · Bespoke",
    term: "SMA · Warehouse · Forward Flow",
    domicile: "Cayman Islands · Counterparty",
    targetSize: "USD 250m – 1.0bn+ per programme",
    minCommit: "Programmatic",
    fees: "Documented per programme",
    note:
      "Programmatic credit relationships — separately managed accounts, warehouse facilities and forward-flow arrangements with institutional counterparties.",
  },
  {
    family: "SMA",
    name: "KR Strategic Accounts",
    mandate: "Bespoke · Cross-Mandate",
    term: "Single-investor / SMA",
    domicile: "Cayman Islands · Counterparty",
    targetSize: "USD 250m+ per mandate",
    minCommit: "By engagement",
    fees: "Bespoke",
    note:
      "Documented, single-investor mandates tailored to the strategic and regulatory needs of sophisticated institutional capital.",
  },
];

const FAMILIES = ["Fund", "Capital Channel", "Business Line", "SMA"];

const FundsPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid={LANDING.fundsPage}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Funds"
          label="Fund Architecture · Vehicle Stack"
          title={
            <>
              Nine vehicles.{" "}
              <span className="italic text-[var(--kr-gold)]">
                Four structures.
              </span>{" "}
              One platform.
            </>
          }
          description="The platform's intended vehicle architecture is calibrated to the cash-flow profile, duration and governance preferences of sophisticated institutional capital. Vehicle names are working titles, subject to documentation and regulatory authorisation."
          breadcrumb="Funds"
        />

        {/* Working titles banner */}
        <section className="pt-16">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="px-6 py-4 border border-[var(--kr-gold)]/40 inline-flex items-center gap-4">
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Working Titles
              </span>
              <span className="w-8 h-px bg-[var(--kr-gold)]/60" />
              <span className="text-[13px] text-[var(--kr-text-muted)]">
                Vehicle names are working titles · in formation. Subject to
                documentation and regulatory authorisation.
              </span>
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-20">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 space-y-16">
            {FAMILIES.map((family) => {
              const items = VEHICLES.filter((v) => v.family === family);
              return (
                <div
                  key={family}
                  data-testid={`${LANDING.fundsPageFamilyPrefix}${family
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                >
                  <div className="flex items-baseline justify-between mb-8 pb-4 border-b border-[var(--kr-line)]">
                    <div className="flex items-center gap-4">
                      <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                        {family}
                      </span>
                      <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
                      <span className="font-mono-label text-[10px] text-[var(--kr-text-muted)]">
                        {String(items.length).padStart(2, "0")} Vehicle
                        {items.length > 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-10">
                    {items.map((v) => (
                      <article
                        key={v.name}
                        data-testid={`${
                          LANDING.fundsPageVehiclePrefix
                        }${v.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="grid lg:grid-cols-12 gap-8 lg:gap-12 pb-10 border-b border-[var(--kr-line-soft)] last:border-b-0 group hover:bg-[rgba(201,169,110,0.018)] transition-colors duration-500 px-2 lg:px-4 -mx-2 lg:-mx-4 py-6 rounded"
                      >
                        <div className="lg:col-span-4">
                          <div className="font-mono-label text-[9px] text-[var(--kr-gold)] mb-3">
                            {v.mandate}
                          </div>
                          <h3 className="font-serif-display text-[var(--kr-text)] text-[1.7rem] leading-[1.2] mb-3 group-hover:text-[var(--kr-gold)] transition-colors duration-500">
                            {v.name}
                          </h3>
                          <span className="font-mono-label text-[9px] text-[var(--kr-text-dim)]">
                            · Working title
                          </span>
                        </div>
                        <div className="lg:col-span-4">
                          <p className="text-[var(--kr-text-muted)] text-[14.5px] leading-[1.8]">
                            {v.note}
                          </p>
                        </div>
                        <dl className="lg:col-span-4 grid grid-cols-2 gap-x-6 gap-y-5">
                          {[
                            ["Term", v.term],
                            ["Domicile", v.domicile],
                            ["Target Size", v.targetSize],
                            ["Min. Commit", v.minCommit],
                            ["Fees", v.fees],
                          ].map(([k, val]) => (
                            <div key={k} className="col-span-2 md:col-span-1">
                              <dt className="font-mono-label text-[9px] text-[var(--kr-text-muted)] mb-1">
                                {k}
                              </dt>
                              <dd className="text-[13px] text-[var(--kr-text)] leading-[1.5]">
                                {val}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      </article>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-[var(--kr-line)] py-24 bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4 mb-6">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  Investor Access
                </span>
                <span className="w-12 h-px bg-[var(--kr-gold)]/60" />
              </div>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.8rem,3.2vw,2.6rem)] leading-[1.15] max-w-[28ch]">
                Vehicle terms and capacity are advanced on a{" "}
                <span className="italic text-[var(--kr-gold)]">
                  documented, confidential
                </span>{" "}
                basis.
              </h2>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-4 lg:justify-end">
              <button
                type="button"
                onClick={openModal}
                data-testid={LANDING.fundsPageRequest}
                className="kr-btn-primary"
              >
                Request Investor Access
                <span aria-hidden>→</span>
              </button>
              <Link to="/strategies" className="kr-btn-ghost">
                Explore Strategies
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default FundsPage;
